import cx from 'classnames'
import styles from './GithubFrame.module.scss'
import { PullResponse } from './types'
import { githubPullIcon, parseDate } from './common'
import { formatDistance, formatISO } from 'date-fns'
import { ReactComponent as CommentsIcon } from './icons/comments.svg'
import { startCase, times } from 'lodash'
import { ReactComponent as CommitIcon } from './icons/commit.svg'
import { ReactComponent as FilesChangedIcon } from './icons/filesChanged.svg'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { StrongNode, nodes } from 'prose-core'
import { AnchorOrDiv, Skel } from '../EmbedCommon/common'

export type GithubFrameProps = {
  node: StrongNode<typeof nodes.github_embed>
  isLoading: boolean
  status: 'merged' | 'open' | 'open-issue' | 'closed' | 'closed-issue' | 'draft'
  data: PullResponse
  preview?: true
}
export const GithubFrame = (props: GithubFrameProps) => {
  let { node, isLoading, status, data, preview } = props

  return (
    <div className={cx(styles.pull, styles[node.attrs.mode], { [styles.isLoading]: isLoading })}>
      <AnchorOrDiv link={preview ? undefined : node?.attrs.link} className={styles.title}>
        {isLoading ? (
          <>
            <Skel height={'26px'} />
            <Skel width={'75%'} height={'26px'} />
          </>
        ) : (
          <>
            {data?.title}
            <span className={styles.number}> #{data?.number}</span>
            {data?.labels?.map((label) => (
              <div
                className={styles.label}
                key={label.name}
                style={{
                  color: `#${label.color}`,
                  borderColor: `#${label.color}4C`,
                  backgroundColor: `#${label.color}2D`,
                }}
              >
                {label.name}
              </div>
            ))}
          </>
        )}
      </AnchorOrDiv>
      <div className={styles.meta}>
        {isLoading ? (
          <div className={cx(styles.status, styles.isLoading)}>
            <Skel width={'90px'} height={'36px'} />
            <Skel />
          </div>
        ) : (
          <>
            <div className={cx(styles.status, styles[status])}>
              <div className={styles.icon}>{githubPullIcon(status)}</div>
              {(() => {
                switch (status) {
                  case 'open-issue':
                    return 'Open'
                  case 'closed-issue':
                    return 'Closed'
                  default:
                    return startCase(status)
                }
              })()}
            </div>
            <span className={styles.intent}>
              {(() => {
                switch (status) {
                  case 'open':
                  case 'draft':
                  case 'closed':
                    return (
                      <>
                        {data?.user?.login} wants to merge {data?.commits} commits into{' '}
                        <span className={styles.target}>{data?.base?.ref}</span> from{' '}
                        <span className={styles.source}>{data?.head?.ref}</span>
                      </>
                    )
                  case 'merged':
                    return (
                      <>
                        {data?.user?.login} merged {data?.commits} commits into{' '}
                        <span className={styles.target}>{data?.base?.ref}</span> from{' '}
                        <span className={styles.source}>{data?.head?.ref}</span>
                      </>
                    )
                  case 'closed-issue':
                  case 'open-issue':
                    return (
                      <>
                        {data?.user?.login} opened this issue{' '}
                        <span title={data?.created_at && formatISO(parseDate(data?.created_at))}>
                          {formatDistance(parseDate(data?.created_at ?? new Date()), new Date(), {
                            addSuffix: true,
                          })}
                        </span>
                        {data?.comments === 1 && ` · 1 comment`}
                        {data?.comments > 1 && ` · ${data?.comments} comments`}
                      </>
                    )
                }
              })()}
            </span>
          </>
        )}
      </div>

      {!['open-issue', 'closed-issue'].includes(status) && (
        <div className={cx(styles.tabsPane, { [styles.isLoading]: isLoading })}>
          {isLoading ? (
            <>
              <Skel width={'100%'} />
              <Skel width={'100%'} />
              <Skel width={'100%'} />
              <Skel width={'100%'} />
              <Skel width={'50%'} />
            </>
          ) : (
            <>
              <div className={styles.tab}>
                <div className={styles.icon}>
                  <CommentsIcon height={16} />
                </div>
                <div className={styles.title}>Conversation</div>
                <div className={styles.num}>{data?.comments}</div>
              </div>
              <div className={styles.tab}>
                <div className={styles.icon}>
                  <CommitIcon height={16} />
                </div>
                <div className={styles.title}>Commits</div>
                <div className={styles.num}>{data?.commits}</div>
              </div>
              <div className={styles.tab}>
                <div className={styles.icon}>
                  <FilesChangedIcon height={16} />
                </div>
                <div className={styles.title}>Files Changed</div>
                <div className={styles.num}>{data?.changed_files}</div>
              </div>
              <ChangesWidget additions={data?.additions} deletions={data?.deletions} />
            </>
          )}
        </div>
      )}
      <div className={styles.bodyPane}>
        {isLoading ? (
          <Skel className={styles.avatar} height={'24px'} />
        ) : (
          <img
            src={data?.user?.avatar_url}
            width={24}
            height={24}
            alt={`${data?.user?.login}'s avatar`}
            className={styles.avatar}
          />
        )}
        <div className={styles.content}>
          <div className={styles.heading}>
            {isLoading ? (
              ' '
            ) : (
              <>
                <span className={styles.login}>{data?.user?.login}</span> commented{' '}
                <span title={data?.created_at && formatISO(parseDate(data?.created_at))}>
                  {formatDistance(parseDate(data?.created_at), new Date(), {
                    addSuffix: true,
                  })}
                </span>
              </>
            )}
          </div>
          <div className={cx(styles.body, { [styles.isLoading]: isLoading })}>
            {isLoading ? (
              <>
                <Skel width="20%" />
                <Skel width="100%" />
                <Skel width="70%" />
              </>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.body}</ReactMarkdown>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ChangesWidget = (props: { additions: number; deletions: number }) => {
  const { additions, deletions } = props
  const total = additions + deletions

  return (
    <div className={styles.changesWidget}>
      <div className={styles.additions}>+{additions}</div>
      <div className={styles.deletions}>-{deletions} </div>
      {total > 5 ? (
        times(5, (i) => {
          if (i < Math.floor(additions / 5) * 5) {
            return <div className={cx(styles.block, styles.addition)} />
          } else {
            return <div className={cx(styles.block, styles.deletion)} />
          }
        })
      ) : (
        <>
          {times(additions, () => (
            <div className={cx(styles.block, styles.addition)} />
          ))}
          {times(deletions, () => (
            <div className={cx(styles.block, styles.deletion)} />
          ))}
          {times(5 - total, () => (
            <div className={cx(styles.block, styles.neutral)} />
          ))}
        </>
      )}
    </div>
  )
}
